import React from 'react';
import { motion } from 'framer-motion';
import { Award, Users, TrendingUp, Lightbulb, ExternalLink, ArrowRight, Linkedin, Mail } from 'lucide-react';

const About: React.FC = () => {
  const achievements = [
    {
      icon: <Award className="w-6 h-6" />,
      title: "Patent Co-Inventor",
      description: "Named as co-inventor for two patents - one issued for 'Performing Iterative Entity Discovery and Instrumentation' and filed for 'Creating and Maintaining an Entity Instrumentation Inventory'"
    },
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Industry Recognition",
      description: "Frequent speaker at industry conferences such as KubeCon and .conf, showcasing expertise in product strategy, public speaking, open source and Observability"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Leadership Impact",
      description: "Mentored two Product Managers, fostering their career growth and enabling successful delivery of high-priority features aligned with key adoption OKRs"
    },
    {
      icon: <Lightbulb className="w-6 h-6" />,
      title: "Innovation Driver",
      description: "Tripled OpenTelemetry customer adoption for Splunk Observability and played a key role in Splunk being named as a Leader in the 2023, 2024 Gartner Magic Quadrant for APM"
    }
  ];

  return (
    <section id="about" className="pt-48 pb-16 bg-white">
      <div className="container-max">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
          {/* Left Column: Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              About Me
            </h2>
            
            <div className="space-y-6 text-gray-600">
              <p className="text-lg leading-relaxed">
                As a Product Manager with a strong Software Engineering background, I lead cross functional teams at Splunk, a Cisco company, to deliver impactful solutions that are simple, reliable, and easy to adopt.
              </p>
              
              <p className="text-lg leading-relaxed">
                Earlier in my career, I built and scaled consumer products at Shutterfly and Chewy, which strengthened my ability to translate complexity into customer value.
              </p>
              
              <p className="text-lg leading-relaxed">
                Beyond shipping results, I mentor early career PMs - guiding them about career development, product strategy and execution. I'm a frequent speaker at industry conferences such as KubeCon, .conf focusing on topics related to observability and product-led growth.
              </p>

              <p className="text-lg leading-relaxed">
                I enjoy solving challenging problems and back myself to do so. I strongly believe in bringing clarity to ambiguous situations, balancing strategy with execution, and delivering measurable impact for both customers and the business.
              </p>

              {/* Call to Action Buttons & Social Links moved here */}
              <div className="pt-8 space-y-8">
                <div className="flex flex-wrap gap-4">
                  <motion.a
                    href="#contact"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-primary inline-flex items-center gap-2 shadow-lg shadow-primary-200"
                  >
                    Let's Build Together
                    <ArrowRight className="w-4 h-4" />
                  </motion.a>
                  
                  <motion.a
                    href="#projects"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="btn-secondary inline-flex items-center gap-2"
                  >
                    View My Work
                  </motion.a>
                </div>

                <div className="flex items-center space-x-6">
                  <motion.a
                    href="https://www.linkedin.com/in/aunsh-chaudhari/"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1, y: -2 }}
                    className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
                  >
                    <Linkedin className="w-6 h-6" />
                  </motion.a>
                  
                  <motion.a
                    href="mailto:aunsh04@gmail.com"
                    whileHover={{ scale: 1.1, y: -2 }}
                    className="text-gray-600 hover:text-primary-600 transition-colors duration-200"
                  >
                    <Mail className="w-6 h-6" />
                  </motion.a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Profile Image */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="flex justify-center items-center h-full"
          >
            <div className="relative group">
              <div className="w-full max-w-sm aspect-[3/4] rounded-2xl overflow-hidden shadow-2xl border-4 border-white transition-transform duration-500 group-hover:scale-[1.02]">
                <img
                  src="/images/aunsh-marathon.png"
                  alt="Aunsh Chaudhari"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=1000&fit=crop";
                  }}
                />
              </div>
              {/* Decorative background elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary-100 rounded-full -z-10 opacity-60"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary-50 rounded-full -z-10 opacity-60"></div>
            </div>
          </motion.div>
        </div>

        {/* Achievements Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-8"
        >
          {achievements.map((achievement, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 border border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center text-primary-600">
                    {achievement.icon}
                  </div>
                  <h3 className="font-semibold text-gray-900">
                    {achievement.title}
                  </h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {achievement.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Education & Mentorship Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 bg-gray-50 rounded-xl p-8 border border-gray-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Award className="w-6 h-6 text-primary-600" />
                Education
              </h3>
              <div className="space-y-6">
                <div className="relative pl-6 border-l-2 border-primary-200">
                  <p className="font-bold text-gray-900">Master of Science in Computer Science</p>
                  <p className="text-primary-700 font-medium">Northeastern University, Boston, USA</p>
                  <p className="text-sm text-gray-500 mb-2">December 2018</p>
                  <p className="text-sm text-gray-600 leading-relaxed bg-white p-3 rounded-lg border border-gray-100 italic">
                    "Won Best User Experience award for 'Charles - Intelligent Chatbot' at GE Aviation Hackathon Challenge"
                  </p>
                </div>
                <div className="relative pl-6 border-l-2 border-primary-100">
                  <p className="font-bold text-gray-900">Bachelor of Engineering in Computer Engineering</p>
                  <p className="text-primary-700 font-medium">University of Mumbai, Mumbai, India</p>
                  <p className="text-sm text-gray-500">May 2016</p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <Users className="w-6 h-6 text-primary-600" />
                Mentorship & Impact
              </h3>
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
                  <p className="font-semibold text-gray-900 mb-2">ADPList Top Mentor</p>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    Active mentor guiding Product Managers on career development, strategy, and execution. 
                    Passionate about giving back to the community and fostering the next generation of product leaders.
                  </p>
                  <motion.a
                    href="https://adplist.org/mentors/aunsh-chaudhari"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 transition-colors"
                  >
                    View Mentor Profile <ExternalLink className="w-4 h-4" />
                  </motion.a>
                </div>
                
                <div className="flex flex-wrap gap-2 pt-2">
                  <span className="px-3 py-1 bg-white border border-primary-100 text-primary-800 text-xs font-medium rounded-full">Pragmatic Certified</span>
                  <span className="px-3 py-1 bg-white border border-primary-100 text-primary-800 text-xs font-medium rounded-full">AWS Certified Solutions Architect</span>
                  <span className="px-3 py-1 bg-white border border-primary-100 text-primary-800 text-xs font-medium rounded-full">Speech & Drama Certified</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
